import { Card, CardContent } from '@pesto/ui/components/Card';
import { Typography } from '@pesto/ui/components/Typography';
import { MapPin } from 'lucide-react';

import type { EntityAddress, EntityAddressCardConfig } from '../types/EntityAddressCard.types';

import { EntityHeader } from './EntityHeader';

interface EntityAddressCardProps {
  config: EntityAddressCardConfig;
}

const renderAddressLines = (address: EntityAddress | null, emptyText: string) => {
  if (!address) {
    return (
      <Typography variant="body" className="text-muted-foreground italic">
        {emptyText}
      </Typography>
    );
  }

  const cityLine = [address.city, address.state, address.zip].filter(Boolean).join(', ').replace(', ,', ',');

  return (
    <div className="space-y-0.5">
      {address.addressFirstLine && <Typography variant="body">{address.addressFirstLine}</Typography>}
      {address.addressSecondLine && <Typography variant="body">{address.addressSecondLine}</Typography>}
      {cityLine && <Typography variant="body">{cityLine}</Typography>}
      {address.isPoBox && (
        <Typography variant="caption" className="text-muted-foreground">
          PO Box
        </Typography>
      )}
    </div>
  );
};

export const EntityAddressCard = ({ config }: EntityAddressCardProps) => {
  const { title = 'Address', description, sections, emptyText = 'No address on file', actions } = config;
  const showSectionLabels = sections.length > 1;
  const gridClass = sections.length > 1 ? 'grid gap-6 md:grid-cols-2' : 'grid gap-6';

  return (
    <Card>
      <EntityHeader
        config={{
          avatar: { icon: MapPin },
          title,
          description,
          actions,
        }}
      />
      <CardContent className={gridClass}>
        {sections.map((section) => (
          <div key={section.label} className="space-y-2">
            {showSectionLabels && (
              <Typography variant="caption" className="text-muted-foreground/80 font-medium uppercase tracking-wide">
                {section.label}
              </Typography>
            )}
            {renderAddressLines(section.address, emptyText)}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
